import { focusPinIcon, placePinIcon, PIN_HEIGHT } from '../place-pin';
import { MapFocusObject, ensureMapObjectPane } from '../map-object-focus';
// Keep the default import: leaflet.markercluster mutates Leaflet's CommonJS
// module.exports. Under Angular's esbuild pipeline, a namespace import is a
// snapshot and does not see markerClusterGroup after that mutation.
import L from 'leaflet';
import 'leaflet.markercluster';
import { CacheService } from '../../../services/cache.service';
import { boundsPath, pathsIntersect } from '../spatial/area-intersection';
import {
  map as rxMap,
  filter,
  Observable,
  of,
  map,
  tap,
} from 'rxjs';

import {
  MapObjectDetails,
  MapObjectDetailsService,
} from '../../../services/map-object-details.service';
import {
  countBadgeFontPx,
  countBadgeIcon,
  countBadgeSize,
} from '../count-badge';
import {
  parsePlaceClusterId,
  parsePointSelectionId,
  placeClusterId,
  pointSelectionId,
  PlaceClusterSelection,
  PlaceClusterId,
  PointSelectionId,
  PointObjectSelection,
} from '../point-object-selection';
import { PlaceMarkerOwner, PlaceMarkerLayoutService } from '../place-marker-layout.service';
import { MAP_CLUSTER_MODE_SWITCH_ZOOM, MapServerCluster, MapViewport } from '../map-tile-cache';
import type { MapTile } from '../spatial/map-viewport';
import { latLngToTile, tileBoundsAtZoom } from '../spatial/tile-math';
import { MapObject, MapObjectId } from '../map-object';
import {
  MapObjectPopover,
  MapObjectPopoverSection,
} from '../map-object-popover';
import { PopoverFeature } from '../popover-feature';
import { MapNavigationService } from '../map-navigation.service';
import {
  MapObjectSelectionHandler,
  SelectableFeature,
} from '../selectable-feature';
import { GeometryFeatureBase } from './geometry-feature.base';

export interface PlaceMapObject extends MapObject<number> {
  readonly placeId: number;
  readonly lat: number;
  readonly lng: number;
}

export interface PlaceFeatureOptions {
  readonly key: string;
  readonly name: string;
  readonly color: string;
}

/** A bounded change to map objects already owned by one Place-backed feature. */
export interface PlaceFeaturePatch<
  TObject extends PlaceMapObject,
> {
  readonly upsert?: readonly TObject[];
  readonly remove?: readonly number[];
  readonly total?: number;
}

export interface MapSelectionObject {
  readonly id: number;
  readonly placeId: number;
  readonly lat: number;
  readonly lng: number;
}

export interface PlaceFeatureLocalSelection {
  readonly objectIds: readonly number[];
  readonly clusters: readonly PlaceClusterSelection[];
}

interface PlaceFeatureReplacement<TObject> {
  readonly objects: readonly TObject[];
  readonly clusters: readonly MapServerCluster[];
  readonly serverClusterMode: boolean;
  readonly total?: number;
}

const MAX_CACHED_OBJECT_DETAILS = 200;

interface MarkerClusterAnimationState {
  readonly _inZoomAnimation?: number;
}

interface RenderedMarker {
  readonly marker: L.Marker;
  readonly changed: boolean;
}

/**
 * Common marker and cluster lifecycle for one Place-backed business feature.
 *
 * Each concrete feature owns one instance, one map-object set and one cluster group.
 * Selection contains this feature's business IDs; visible Pins and clusters share one display layout per map.
 */
export abstract class PlaceFeatureBase<
  TObject extends PlaceMapObject,
> extends GeometryFeatureBase<TObject>
implements SelectableFeature<TObject>, PopoverFeature<TObject> {
  private selectionEnabled = false;
  private popoversEnabled = false;
  readonly geometryType = 'place' as const;
  readonly selection = new PointObjectSelection();
  get selectedObjectIds(): readonly number[] { return this.selection.objectIds; }
  readonly selectionCount$ = this.selection.changed$.pipe(
    rxMap(() => this.selection.size),
  );
  readonly key: string;
  readonly name: string;
  override readonly iconColor: string;
  override readonly icon = 'location_on';

  /**
   * Objects of this feature that somebody clicked inside a map popover.
   *
   * The mirror of ShowOnMapDirective: a row asks the map to show an object,
   * and this is the map asking for an object to be shown in the list. The
   * navigation service carries every feature's clicks, so the narrowing to
   * this one happens here - a page subscribes to its own layer and gets
   * ids it can look up, never another feature's.
   */
  readonly revealRequests$: Observable<MapObjectId>;

  objects: TObject[] = [];
  loading = false;
  count: number | null = null;

  private readonly objectsById = new Map<number, TObject>();
  private readonly objectIndexes = new Map<number, number>();
  private readonly markers = new Map<number, L.Marker>();
  private readonly markerObjects = new WeakMap<L.Marker, TObject>();
  /** Last visual state applied to each marker; business-only DTO changes do
   *  not require replacing its DOM icon or rebuilding its cluster. */
  private readonly markerVisuals = new WeakMap<L.Marker, string>();
  private readonly clusterLayer: L.MarkerClusterGroup;
  private readonly pinOwner: PlaceMarkerOwner;
  private map?: L.Map;

  /** The live map projects rendered icon bounds, including their display offsets. */
  useMap(map: L.Map): void {
    if (this.map === map) return;
    this.markerLayout.clear(this.map, this.pinOwner);
    this.map = map;
    ensureMapObjectPane(map, 'map-object-selection', 620);
    for (const [id, marker] of this.markers) {
      this.markerLayout.addMarker(map, marker, this.pinOwner, id);
    }
    for (const [id, marker] of this.serverClusterMarkers) {
      this.markerLayout.addMarker(map, marker, this.pinOwner, id);
    }
    map.once('unload', () => {
      this.markerLayout.clear(map, this.pinOwner);
      if (this.map !== map) return;
      if (this.ownsFocus()) this.navigation.clearFocus();
      this.map = undefined;
    });
  }
  private readonly serverClusterMarkers =
    new Map<PlaceClusterId, L.Marker>();
  private readonly serverClusterByMarker =
    new WeakMap<L.Marker, MapServerCluster>();
  private readonly serverClusterVisuals = new WeakMap<L.Marker, string>();
  private readonly selectionCoordinates =
    new Map<number, { readonly placeId: number; readonly lat: number; readonly lng: number }>();
  /** Current viewport projection of directly selected objects by server tile. */
  private readonly directSelectedObjectIdsByClusterId =
    new Map<PlaceClusterId, Set<number>>();
  private serverClusters = new Map<PlaceClusterId, MapServerCluster>();
  private readonly serverClusterZooms = new Set<number>();
  private serverClusterMode = false;
  private pendingReplacement?: PlaceFeatureReplacement<TObject>;
  private pendingPatches: PlaceFeaturePatch<TObject>[] = [];
  private objectSelectionHandler: MapObjectSelectionHandler | null = null;

  protected constructor(
    options: PlaceFeatureOptions,
    private readonly details: MapObjectDetailsService,
    private readonly markerLayout: PlaceMarkerLayoutService,
    protected readonly cache: CacheService,
    private readonly navigation: MapNavigationService,
  ) {
    super();
    this.key = options.key;
    this.revealRequests$ = navigation.revealRequests$.pipe(
      filter(reference => reference.featureKey === this.key),
      rxMap(reference => reference.objectId),
    );
    this.name = options.name;
    this.iconColor = options.color;
    this.clusterLayer = L.markerClusterGroup({
      chunkedLoading: true,
      removeOutsideVisibleBounds: true,
      maxClusterRadius: 70,
      disableClusteringAtZoom: MAP_CLUSTER_MODE_SWITCH_ZOOM,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      iconCreateFunction: cluster => this.createClusterIcon(cluster),
    });
    this.layer.addLayer(this.clusterLayer);

    this.pinOwner = {
      key: this.key,
      group: this.clusterLayer,
      color: this.iconColor,
    };

    this.clusterLayer.on('animationend', () => {
      queueMicrotask(() => {
        if (this.clusterAnimationActive()) return;
        this.flushPendingData();
      });
    });

    this.clusterLayer.on('clusterclick', event => {
      const input = (event as L.LeafletMouseEvent).originalEvent;
      const cluster = (event as L.LeafletEvent & {
        layer: L.MarkerCluster;
      }).layer;
      if (!this.enabled || (!this.popoversEnabled && !this.selectionEnabled)) return;
      const childMarkers = cluster.getAllChildMarkers() as L.Marker[];
      const objects = childMarkers
        .map(marker => this.markerObjects.get(marker))
        .filter((object: TObject | undefined): object is TObject =>
          object !== undefined);
      const serverClusters = childMarkers
        .map(marker => this.serverClusterByMarker.get(marker))
        .filter((item): item is MapServerCluster => item !== undefined);
      if (!objects.length && !serverClusters.length) return;
      this.objectSelectionHandler?.([
        ...objects.map(object => pointSelectionId(object.id, null)),
        ...serverClusters.map(item => pointSelectionId(
          null, placeClusterId(this.key, item),
        )),
      ], input);
    });

    this.selection.changed$.subscribe(changedIds =>
      this.renderObjectSelection(changedIds));
  }

  replaceObjects(objects: readonly TObject[], total?: number): void {
    this.replaceData({
      objects,
      clusters: [],
      serverClusterMode: false,
      total,
    });
  }

  replaceClusters(
    clusters: readonly MapServerCluster[],
    total?: number,
  ): void {
    this.replaceClusteredObjects([], clusters, total);
  }

  /**
   * Replaces one low-zoom viewport with its sparse points and dense tile
   * representatives. Both marker types share the same MarkerClusterGroup so
   * Leaflet performs the final screen-distance clustering without overlap.
   */
  replaceClusteredObjects(
    objects: readonly TObject[],
    clusters: readonly MapServerCluster[],
    total?: number,
  ): void {
    this.replaceData({
      objects,
      clusters,
      serverClusterMode: true,
      total,
    });
  }

  private replaceData(replacement: PlaceFeatureReplacement<TObject>): void {
    this.pendingPatches = [];
    if (this.clusterAnimationActive()) {
      this.pendingReplacement = replacement;
      return;
    }
    this.pendingReplacement = undefined;
    this.applyReplacement(replacement);
  }

  private applyReplacement(
    replacement: PlaceFeatureReplacement<TObject>,
  ): void {
    const { objects, clusters, serverClusterMode, total } = replacement;
    this.serverClusterMode = serverClusterMode;
    this.serverClusters = new Map(clusters.map(cluster => [
      placeClusterId(this.key, cluster),
      cluster,
    ]));
    this.serverClusterZooms.clear();
    for (const cluster of clusters) this.serverClusterZooms.add(cluster.zoom);
    this.objects = [];
    if (total !== undefined) this.count = total;
    this.objectsById.clear();
    this.objectIndexes.clear();
    for (const object of objects) this.upsertObject(object);
    this.rememberVisibleSelectionCoordinates(
      objects.map(object => object.id),
    );
    this.render();
    this.notifyChanged();
  }

  private clusterAnimationActive(): boolean {
    return ((this.clusterLayer as unknown as MarkerClusterAnimationState)
      ._inZoomAnimation ?? 0) > 0;
  }

  private flushPendingData(): void {
    // Viewport replacements and patches wait for cluster zoom animations.
    if ((!this.pendingReplacement && !this.pendingPatches.length)
      || this.clusterAnimationActive()) return;
    const pending = this.pendingReplacement;
    this.pendingReplacement = undefined;
    if (pending) this.applyReplacement(pending);
    const patches = this.pendingPatches;
    this.pendingPatches = [];
    for (const patch of patches) this.patchObjects(patch);
  }

  /**
   * Applies a small server or realtime change without reconciling every
   * currently visible object. Full viewport/filter responses continue to use
   * replaceObjects().
   */
  patchObjects(patch: PlaceFeaturePatch<TObject>): void {
    if (this.clusterAnimationActive() || this.pendingReplacement) {
      this.pendingPatches.push(patch);
      return;
    }
    const previousSize = this.objectsById.size;
    const affectedIds = new Set<number>();

    for (const id of patch.remove ?? []) {
      if (this.ownsFocus() && this.navigation.focusedObject?.objectId === id) this.navigation.clearFocus();
      if (this.removeObject(id)) affectedIds.add(id);
    }
    for (const object of patch.upsert ?? []) {
      const id = this.objectId(object);
      this.upsertObject(object);
      affectedIds.add(id);
    }

    if (patch.total !== undefined) {
      this.count = patch.total;
    } else if (this.count !== null) {
      this.count = Math.max(0,
        this.count + this.objectsById.size - previousSize);
    }

    if (this.enabled && affectedIds.size > 0) {
      this.renderObjects(affectedIds);
    }
    this.notifyChanged();
  }

  setLoading(loading: boolean): void {
    if (this.loading === loading) return;
    this.loading = loading;
    this.notifyChanged();
  }

  render(): void {
    if (!this.enabled) return;
    this.rememberVisibleSelectionCoordinates(this.objectsById.keys());
    this.pruneSelectionCoordinates();
    this.rebuildDirectSelectionClusterIndex();
    if (!this.layer.hasLayer(this.clusterLayer)) {
      this.layer.addLayer(this.clusterLayer);
    }

    for (const id of this.markers.keys()) {
      if (!this.objectsById.has(id)) this.removeMarker(id);
    }

    const renderedMarkers: L.Marker[] = [];
    for (const [id, object] of this.objectsById) {
      const rendered = this.renderObject(id, object);
      if (rendered.changed) renderedMarkers.push(rendered.marker);
    }

    // Passing the changed markers avoids markercluster walking its private
    // root tree before the group has been attached to a real map.
    if (renderedMarkers.length > 0) {
      this.clusterLayer.refreshClusters(renderedMarkers);
    }
    this.renderServerClusters();
  }

  override clear(): void {
    this.markerLayout.clear(this.map, this.pinOwner);
    this.clusterLayer.clearLayers();
    this.markers.clear();
    this.serverClusterMarkers.clear();
    this.directSelectedObjectIdsByClusterId.clear();
    if (!this.layer.hasLayer(this.clusterLayer)) {
      this.layer.addLayer(this.clusterLayer);
    }
  }

  resetSession(): void {
    if (this.ownsFocus()) this.navigation.clearFocus();
    this.selection.clear();
    this.disablePopovers();
    this.pendingReplacement = undefined;
    this.selectionCoordinates.clear();
    this.clear();
    this.applyReplacement({ objects: [], clusters: [], serverClusterMode: false });
    this.count = null;
    this.setLoading(false);
    this.notifyChanged();
  }

  /** Business detail shown below the Place shared by this map object. */
  protected abstract objectPopover(
    object: TObject,
  ): MapObjectPopoverSection;
  protected abstract objectId(object: TObject): number;
  protected abstract selectObjects(
    bounds: L.LatLngBounds,
    polygon?: readonly L.LatLng[],
    clusterTiles?: readonly MapTile[],
  ): Observable<readonly MapSelectionObject[]>;

  abstract onViewportChanged(viewport: MapViewport): void;
  abstract clearCacheAndRefresh(): void;

  reload(): void {
    this.clearCacheAndRefresh();
  }

  enableSelection(): void {
    this.selectionEnabled = true;
  }

  disableSelection(): void {
    this.selectionEnabled = false;
  }

  enablePopovers(): void {
    this.popoversEnabled = true;
  }

  disablePopovers(): void { this.popoversEnabled = false; }

  setObjectSelectionHandler(handler: MapObjectSelectionHandler | null): void {
    this.objectSelectionHandler = handler;
  }

  selectInBounds(bounds: L.LatLngBounds): void {
    this.addSelection(this.selectionInBounds(bounds));
  }

  clearSelection(): void {
    this.selection.clear();
  }

  selectObjectById(id: string | number): void {
    const objectId = Number(id);
    if (Number.isSafeInteger(objectId)) this.selection.append([pointSelectionId(objectId, null)]);
  }

  isObjectSelected(id: string | number): boolean {
    if (typeof id !== 'number') return false;
    const point = this.pointForObject(id);
    return point ? this.selection.isObjectSelected(this.key, id, point.lat, point.lng)
      : this.selection.hasObject(id);
  }

  setObjectsSelected(ids: readonly (string | number)[], selected: boolean): void {
    const objects = ids.flatMap(id => {
      if (typeof id !== 'number') return [];
      const point = this.pointForObject(id);
      return point ? [{ id, lat: point.lat, lng: point.lng }] : [];
    });
    this.selection.setObjectsSelected(this.key, objects, selected);
    // Table rows can select business IDs before their map geometry is loaded.
    // Keep these as direct IDs; never invent membership in a cluster.
    const direct = ids.filter((id): id is number =>
      typeof id === 'number' && Number.isSafeInteger(id) && !this.pointForObject(id))
      .map(id => pointSelectionId(id, null));
    if (selected) this.selection.append(direct);
    else this.selection.remove(direct);
  }

  inspectObject(id: string | number): void {
    const object = this.objectsById.get(Number(id));
    if (object) this.focusObject(object);
  }

  matchesObjectSelection(ids: readonly (string | number)[]): boolean {
    const target = this.objectSelectionTarget(ids);
    const count = target.objects.length
      + target.clusters.reduce((total, cluster) => total + cluster.count, 0);
    return count > 0 && this.selection.count === count
      && this.isSelectionTargetSelected(target.objects, target.clusters);
  }

  private isSelectionTargetSelected(
    objects: readonly TObject[], clusters: readonly PlaceClusterSelection[],
  ): boolean {
    // Count inherited cluster selection and directly selected objects alike.
    return objects.every(object => this.isMapObjectSelected(object))
      && clusters.every(cluster => {
        const rendered = this.serverClusters.get(placeClusterId(this.key, cluster));
        return !!rendered
          && this.selectedCountForServerCluster(rendered) === cluster.count;
      });
  }

  toggleObjectSelection(ids: readonly (string | number)[]): void {
    const target = this.objectSelectionTarget(ids);
    // A partially selected rendered cluster becomes fully selected on click.
    const selected = this.isSelectionTargetSelected(target.objects, target.clusters);
    this.selection.setObjectsAndClustersSelected(
      this.key,
      target.objects,
      target.clusters,
      !selected,
    );
  }

  appendObjectSelection(ids: readonly (string | number)[]): void {
    const target = this.objectSelectionTarget(ids);
    this.selection.setObjectsAndClustersSelected(
      this.key,
      target.objects,
      target.clusters,
      true,
    );
  }

  private addSelection(selected: PlaceFeatureLocalSelection): void {
    const objectIds = new Set(selected.objectIds);
    this.selection.setObjectsAndClustersSelected(
      this.key,
      [...this.objectsById.values()].filter(object =>
        objectIds.has(object.id)),
      selected.clusters,
      true,
    );
  }

  selectionInBounds(bounds: L.LatLngBounds): PlaceFeatureLocalSelection {
    return this.localSelection(boundsPath(bounds));
  }

  selectionInPath(
    path: readonly L.LatLng[],
  ): PlaceFeatureLocalSelection {
    return this.localSelection(path);
  }

  resolveClusterSelection(
    clusters: readonly PlaceClusterSelection[],
  ): Observable<readonly MapSelectionObject[]> {
    const ownClusters = clusters.filter(cluster =>
      cluster.featureKey === this.key);
    if (ownClusters.length === 0) return of([]);

    const bounds = L.latLngBounds([]);
    for (const cluster of ownClusters) {
      bounds.extend(tileBoundsAtZoom(
        cluster.tileX,
        cluster.tileY,
        cluster.zoom,
      ));
    }

    const clusterTiles = ownClusters.map(cluster => ({
      zoom: cluster.zoom,
      tileX: cluster.tileX,
      tileY: cluster.tileY,
    }));
    return this.selectObjects(bounds, undefined, clusterTiles).pipe(
      tap(objects => this.rememberSelectionCoordinates(objects)),
    );
  }

  resolveSelectedObjects(): Observable<readonly MapSelectionObject[]> {
    const direct = this.selection.objectIds.flatMap(id => {
      const point = this.objectsById.get(id) ?? this.selectionCoordinates.get(id);
      return point ? [{ id, placeId: point.placeId, lat: point.lat, lng: point.lng }] : [];
    });
    return this.resolveClusterSelection(this.selection.clusters).pipe(map(objects => {
      const selected = objects.filter(object => this.selection.isObjectSelected(
        this.key, object.id, object.lat, object.lng,
      ));
      return [...new Map([...direct, ...selected].map(object => [object.id, object])).values()];
    }));
  }

  private localSelection(path: readonly L.LatLng[]): PlaceFeatureLocalSelection {
    const map = this.map;
    if (!this.enabled || !map || path.length < 3) return { objectIds: [], clusters: [] };
    const container = map.getContainer();
    const scale = L.DomUtil.getScale(container);
    const origin = scale.boundingClientRect;
    const hits = new Map<L.Marker, boolean>();
    const containsRendered = (marker: L.Marker | undefined): boolean => {
      const visible = marker && this.clusterLayer.getVisibleParent(marker);
      const element = visible?.getElement();
      if (!visible || !element?.isConnected) return false;
      let hit = hits.get(visible);
      if (hit === undefined) {
        const style = getComputedStyle(element);
        if (style.visibility === 'hidden' || style.opacity === '0') {
          hits.set(visible, false);
          return false;
        }
        const rect = element.getBoundingClientRect();
        const at = (x: number, y: number) => map.containerPointToLatLng([
          (x - origin.left) / scale.x - container.clientLeft,
          (y - origin.top) / scale.y - container.clientTop,
        ]);
        hit = rect.width > 0 && rect.height > 0 && pathsIntersect(path, [
          at(rect.left, rect.top), at(rect.right, rect.top),
          at(rect.right, rect.bottom), at(rect.left, rect.bottom),
        ]);
        hits.set(visible, hit);
      }
      return hit;
    };
    return {
      objectIds: [...new Set([...this.objectsById.values()]
        .filter(object => containsRendered(this.markers.get(this.objectId(object))))
        .map(object => object.id))],
      clusters: this.serverClusterMode
        ? [...this.serverClusters.values()]
          .filter(cluster => containsRendered(
            this.serverClusterMarkers.get(placeClusterId(this.key, cluster)),
          ))
          .map(cluster => this.clusterSelection(cluster))
        : [],
    };
  }

  private renderObjects(ids: ReadonlySet<number>): void {
    if (!this.layer.hasLayer(this.clusterLayer)) {
      this.layer.addLayer(this.clusterLayer);
    }

    const changedMarkers: L.Marker[] = [];
    for (const id of ids) {
      const object = this.objectsById.get(id);
      if (!object) {
        this.removeMarker(id);
        continue;
      }
      const rendered = this.renderObject(id, object);
      if (rendered.changed) changedMarkers.push(rendered.marker);
    }

    if (changedMarkers.length > 0) {
      this.clusterLayer.refreshClusters(changedMarkers);
    }
  }

  private renderObjectSelection(changedIds: readonly PointSelectionId[]): void {
    if (!this.enabled) return;
    const objectIds = new Set<number>();
    const clusterIds = new Set<PlaceClusterId>();
    for (const id of changedIds) {
      const [objectId, clusterId] = parsePointSelectionId(id);
      if (objectId !== null) objectIds.add(objectId);
      if (clusterId !== null) clusterIds.add(clusterId);
    }
    this.rememberVisibleSelectionCoordinates(objectIds);

    const affectedObjectIds = this.objectIdsForClusters(clusterIds);
    for (const objectId of objectIds) affectedObjectIds.add(objectId);
    if (affectedObjectIds.size > 0) this.renderObjects(affectedObjectIds);
    const affectedClusters = this.updateDirectSelectionClusterIndex(objectIds);
    const clustersAffectedByClusterSelection =
      this.findServerClusterKeysByClusters(clusterIds);
    for (const key of clustersAffectedByClusterSelection) {
      affectedClusters.add(key);
    }
    if (clusterIds.size) {
      this.pruneSelectionCoordinates();
      this.rebuildDirectSelectionClusterIndex();
    } else {
      this.pruneSelectionCoordinates(objectIds);
    }
    this.renderServerClusters(affectedClusters);
  }

  private renderObject(id: number, object: TObject): RenderedMarker {
    const target = L.latLng(object.lat, object.lng);
    let marker = this.markers.get(id);
    const visual = this.markerVisual(object);
    if (!marker) {
      marker = L.marker(target, { icon: this.createMarkerIcon(object) });
      marker.on('add', () => this.updateMarkerPane(marker!, this.isMapObjectSelected(this.markerObjects.get(marker!)!)));
      marker.on('click', event =>
        this.onMarkerClick(id, (event as L.LeafletMouseEvent).originalEvent));
      this.markers.set(id, marker);
      this.markerObjects.set(marker, object);
      this.markerVisuals.set(marker, visual);
      this.clusterLayer.addLayer(marker);
      if (this.map) this.markerLayout.addMarker(this.map, marker, this.pinOwner, id);
      return { marker, changed: true };
    }

    const previous = this.markerObjects.get(marker);
    this.markerObjects.set(marker, object);
    this.updateMarkerPane(marker, this.isMapObjectSelected(object));
    // Selection changes only presentation. Coordinates belong to the DTO.
    if (previous?.lat !== object.lat || previous?.lng !== object.lng) {
      marker.setLatLng(target);
    }
    if (this.markerVisuals.get(marker) === visual) {
      return { marker, changed: false };
    }
    marker.setIcon(this.createMarkerIcon(object));
    this.markerLayout.refreshMarker(this.map, marker);
    this.markerVisuals.set(marker, visual);
    return { marker, changed: true };
  }

  private removeMarker(id: number): void {
    const marker = this.markers.get(id);
    if (!marker) return;
    this.markerLayout.removeMarker(this.map, marker);
    this.clusterLayer.removeLayer(marker);
    this.markers.delete(id);
    this.markerObjects.delete(marker);
    this.markerVisuals.delete(marker);
  }

  private markerVisual(object: TObject): string {
    return this.isMapObjectSelected(object) ? 'selected' : '';
  }

  private upsertObject(object: TObject): void {
    const id = this.objectId(object);
    const index = this.objectIndexes.get(id);
    if (index === undefined) {
      this.objectIndexes.set(id, this.objects.length);
      this.objects.push(object);
    } else {
      this.objects[index] = object;
    }
    this.objectsById.set(id, object);
  }

  private removeObject(id: number): boolean {
    const index = this.objectIndexes.get(id);
    if (index === undefined) return false;
    const lastIndex = this.objects.length - 1;
    if (index !== lastIndex) {
      const lastObject = this.objects[lastIndex];
      this.objects[index] = lastObject;
      this.objectIndexes.set(this.objectId(lastObject), index);
    }
    this.objects.pop();
    this.objectIndexes.delete(id);
    this.objectsById.delete(id);
    return true;
  }

  private onMarkerClick(objectId: number, input?: MouseEvent): void {
    const object = this.objectsById.get(objectId);
    const marker = this.markers.get(objectId);
    if (!object || !marker) return;

    if (!this.enabled) return;
    if (!this.popoversEnabled) {
      if (this.selectionEnabled) {
        this.objectSelectionHandler?.([
          pointSelectionId(object.id, null),
        ], input);
      }
      return;
    }

    if (this.selectionEnabled && this.objectSelectionHandler) {
      if (!this.objectSelectionHandler([pointSelectionId(object.id, null)], input)) return;
      if (!this.isMapObjectSelected(object)) return;
    }
    this.focusObject(object);
  }

  private ownsFocus(): boolean {
    return this.navigation.focusedObject?.featureKey === this.key;
  }

  /**
   * Focuses a map object independently of its clustered/visible marker. The
   * point is the object's own Place; the facts in the popover are the
   * business record's, read by the object's id.
   */
  focusObject(object: TObject): void {
    const cached = this.cachedDetails(object);
    const target: MapFocusObject = {
      reference: { featureKey: this.key, objectId: object.id },
      geometry: { type: 'Point', coordinates: [object.lng, object.lat] } as GeoJSON.Point,
      popup: this.createPopover(object, cached),
      pointIcon: focusPinIcon(), popupOffset: L.point(0, -PIN_HEIGHT),
    };
    void this.navigation.focus(target);
    if (cached) return;

    this.cache.load('map-object-details', this.detailsKey(object),
      () => this.details.get(this.key, this.objectId(object)),
      { capacity: MAX_CACHED_OBJECT_DETAILS },
    ).subscribe({
      next: details => {
        this.navigation.updateFocusPopup(target, this.createPopover(object, details));
      },
      error: () => { /* Keep the available object facts when the details fail. */ },
    });
  }

  private createPopover(object: TObject, details?: MapObjectDetails): MapObjectPopover {
    return {
      type: 'Place',
      title: details?.name?.trim() || `${this.name} ${this.objectId(object)}`,
      subtitle: details?.address?.trim()
        || `${object.lat.toFixed(6)}, ${object.lng.toFixed(6)}`,
      values: [
        { label: 'Phone', value: details?.phone },
        { label: 'Email', value: details?.email },
        { label: 'Website', value: details?.website },
      ],
      // The identity is the base's to add rather than each subclass's: every
      // place feature names its record the same way, and a subclass writing
      // its own reference would be one more pair of things to keep in step.
      // It is what lets the popover hand this record back to the page.
      sections: [{
        ...this.objectPopover(object),
        reference: { featureKey: this.key, objectId: this.objectId(object) },
      }],
    };
  }

  private updateMarkerPane(marker: L.Marker, selected: boolean): void {
    const pane = selected ? 'map-object-selection' : 'markerPane';
    marker.options.pane = pane;
    const element = marker.getElement();
    if (element && this.map) this.map.getPane(pane)?.appendChild(element);
  }

  private createMarkerIcon(object: TObject): L.DivIcon {
    return placePinIcon(this.iconColor, this.isMapObjectSelected(object));
  }

  private createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
    const childMarkers = cluster.getAllChildMarkers() as L.Marker[];
    const objects = childMarkers
      .map(marker => this.markerObjects.get(marker))
      .filter((object): object is TObject => object !== undefined);
    // Deduplicated by MAP OBJECT identity - the same unit the server's cluster
    // counts. Keying this on placeId collapsed two Customers at one address
    // into a count of one while both their markers were behind the circle,
    // so the number could never be verified by opening it.
    const objectsById = new Map<number, TObject>();
    for (const object of objects) {
      if (!objectsById.has(this.objectId(object))) {
        objectsById.set(this.objectId(object), object);
      }
    }
    const selectedObjectCount = [...objectsById.values()]
      .filter(object => this.isMapObjectSelected(object)).length;
    const serverClusters = childMarkers
      .map(marker => this.serverClusterByMarker.get(marker))
      .filter((item): item is MapServerCluster => item !== undefined);
    const total = objectsById.size + serverClusters.reduce(
      (sum, item) => sum + item.count,
      0,
    );
    const selectedCount = selectedObjectCount + serverClusters.reduce(
      (sum, item) => sum + this.selectedCountForServerCluster(item),
      0,
    );

    this.updateMarkerPane(cluster, selectedCount > 0);
    this.markerLayout.refreshMarker(this.map, cluster);
    return countBadgeIcon({
      count: total,
      label: selectedCount > 0 ? `${selectedCount}/${total}` : `${total}`,
      color: this.iconColor,
      selected: selectedCount > 0,
    });
  }

  private renderServerClusters(onlyKeys?: ReadonlySet<PlaceClusterId>): void {
    const liveKeys = new Set<PlaceClusterId>();
    const changedMarkers: L.Marker[] = [];
    const renderedClusters = onlyKeys
      ? [...onlyKeys].flatMap(id => {
        const cluster = this.serverClusters.get(id);
        return cluster ? [cluster] : [];
      })
      : [...this.serverClusters.values()];
    const selectedCounts = this.selectedCountsForServerClusters(renderedClusters);
    if (this.serverClusterMode) {
      for (let index = 0; index < renderedClusters.length; index++) {
        const cluster = renderedClusters[index];
        const key = placeClusterId(this.key, cluster);
        liveKeys.add(key);
        let marker = this.serverClusterMarkers.get(key);
        if (!marker) {
          marker = this.createServerClusterMarker(cluster, selectedCounts[index]);
          this.serverClusterMarkers.set(key, marker);
          this.clusterLayer.addLayer(marker);
          if (this.map) this.markerLayout.addMarker(this.map, marker, this.pinOwner, key);
          changedMarkers.push(marker);
        } else if (this.updateServerClusterMarker(
          marker,
          cluster,
          selectedCounts[index],
        )) {
          changedMarkers.push(marker);
        }
      }
    }

    // A selection-only update touches a subset of live markers and must not
    // interpret every untouched key as removed data.
    if (!onlyKeys) {
      for (const [key, marker] of this.serverClusterMarkers) {
        if (liveKeys.has(key)) continue;
        this.serverClusterMarkers.delete(key);
        this.markerLayout.removeMarker(this.map, marker);
        this.clusterLayer.removeLayer(marker);
        this.serverClusterByMarker.delete(marker);
        this.serverClusterVisuals.delete(marker);
      }
    }

    if (changedMarkers.length > 0) {
      this.clusterLayer.refreshClusters(changedMarkers);
    }
  }

  setTotal(total: number): void {
    if (this.count === total) return;
    this.count = total;
    this.notifyChanged();
  }

  private createServerClusterMarker(
    cluster: MapServerCluster,
    selectedCount: number,
  ): L.Marker {
    const marker = L.marker([cluster.lat, cluster.lng]);
    marker.on('click', event => {
      const current = this.serverClusterByMarker.get(marker);
      if (current) {
        this.onServerClusterClick(
          marker,
          current,
          (event as L.LeafletMouseEvent).originalEvent,
        );
      }
    });
    this.updateServerClusterMarker(marker, cluster, selectedCount);
    return marker;
  }

  private updateServerClusterMarker(
    marker: L.Marker,
    cluster: MapServerCluster,
    selectedCount: number,
  ): boolean {
    this.serverClusterByMarker.set(marker, cluster);
    const target = L.latLng(cluster.lat, cluster.lng);
    if (!marker.getLatLng().equals(target)) marker.setLatLng(target);
    const visual = [
      cluster.count,
      selectedCount,
    ].join('/');
    if (this.serverClusterVisuals.get(marker) === visual) return false;
    this.updateMarkerPane(marker, selectedCount > 0);
    marker.setIcon(this.createServerClusterIcon(cluster, selectedCount));
    this.markerLayout.refreshMarker(this.map, marker);
    this.serverClusterVisuals.set(marker, visual);
    return true;
  }

  private onServerClusterClick(
    marker: L.Marker,
    cluster: MapServerCluster,
    input?: MouseEvent,
  ): void {
    if (!this.enabled) return;
    if (this.selectionEnabled) {
      this.objectSelectionHandler?.([
        pointSelectionId(null, placeClusterId(this.key, cluster)),
      ], input);
    }
  }

  private objectSelectionTarget(ids: readonly (string | number)[]): {
    objects: TObject[];
    clusters: PlaceClusterSelection[];
  } {
    const objects = new Map<number, TObject>();
    const clusters = new Map<PlaceClusterId, PlaceClusterSelection>();
    for (const id of ids) {
      if (typeof id !== 'string') continue;
      const [objectId, clusterId] = parsePointSelectionId(id);
      if (objectId !== null) {
        const object = this.objectsById.get(objectId);
        if (object) objects.set(object.id, object);
      }
      if (clusterId !== null) {
        const cluster = this.serverClusters.get(clusterId);
        if (cluster) clusters.set(clusterId, this.clusterSelection(cluster));
      }
    }
    return {
      objects: [...objects.values()],
      clusters: [...clusters.values()],
    };
  }

  private clusterSelection(
    cluster: MapServerCluster,
  ): PlaceClusterSelection {
    return {
      featureKey: this.key,
      zoom: cluster.zoom,
      tileX: cluster.tileX,
      tileY: cluster.tileY,
      count: cluster.count,
    };
  }

  private createServerClusterIcon(
    cluster: MapServerCluster,
    selectedCount: number,
  ): L.DivIcon {
    const selected = selectedCount > 0 ? ' is-selected' : '';
    const label = selectedCount > 0
      ? `${selectedCount}/${cluster.count}`
      : `${cluster.count}`;
    const size = countBadgeSize(cluster.count);

    return L.divIcon({
      className: `map-feature-cluster-icon place-cluster-marker${selected}`,
      html: [
        `<svg class="map-feature-cluster-symbol" viewBox="0 0 40 40" style="--map-feature-color:${this.iconColor}" aria-hidden="true">`,
        '<circle class="map-feature-cluster-symbol__selection-outline" cx="20" cy="20" r="19"/>',
        '<circle class="map-feature-cluster-symbol__body" cx="20" cy="20" r="18"/>',
        `<text class="map-feature-cluster-symbol__count" x="20" y="20" style="font-size:${countBadgeFontPx(label)}px">${label}</text>`,
        '</svg>',
      ].join(''),
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  private selectedCountForServerCluster(
    cluster: MapServerCluster,
  ): number {
    return this.selectedCountsForServerClusters([cluster])[0] ?? 0;
  }

  private selectedCountsForServerClusters(
    clusters: readonly MapServerCluster[],
  ): readonly number[] {
    if (!clusters.length) return [];
    const deferred = this.selection.selectedCountsForClusters(
      this.key,
      clusters.map(cluster => this.clusterSelection(cluster)),
    );
    return clusters.map((cluster, index) => Math.min(
      deferred[index] + (this.directSelectedObjectIdsByClusterId.get(
        placeClusterId(this.key, cluster),
      )?.size ?? 0),
      cluster.count,
    ));
  }

  private updateDirectSelectionClusterIndex(
    objectIds: Iterable<number>,
  ): Set<PlaceClusterId> {
    const keys = new Set<PlaceClusterId>();
    for (const objectId of objectIds) {
      const point = this.pointForObject(objectId);
      if (!point) continue;
      const selected = this.selection.hasObject(objectId)
        && !this.selection.isPointCoveredByCluster(
          this.key,
          point.lat,
          point.lng,
        );
      for (const zoom of this.serverClusterZooms) {
        const tile = latLngToTile(point.lat, point.lng, zoom);
        const clusterId = placeClusterId(this.key, { zoom, ...tile });
        if (!this.serverClusters.has(clusterId)) continue;
        keys.add(clusterId);
        const selectedIds =
          this.directSelectedObjectIdsByClusterId.get(clusterId);
        if (selected) {
          if (selectedIds) selectedIds.add(objectId);
          else {
            this.directSelectedObjectIdsByClusterId.set(
              clusterId,
              new Set([objectId]),
            );
          }
        } else if (selectedIds) {
          selectedIds.delete(objectId);
          if (!selectedIds.size) {
            this.directSelectedObjectIdsByClusterId.delete(clusterId);
          }
        }
      }
    }
    return keys;
  }

  private rebuildDirectSelectionClusterIndex(): void {
    this.directSelectedObjectIdsByClusterId.clear();
    this.updateDirectSelectionClusterIndex(this.selectionCoordinates.keys());
  }

  private findServerClusterKeysByClusters(
    clusterIds: Iterable<PlaceClusterId>,
  ): Set<PlaceClusterId> {
    const changedTiles: MapTile[] = [];
    for (const id of clusterIds) {
      const [featureKey, zoom, tileX, tileY] = parsePlaceClusterId(id);
      if (featureKey === this.key) changedTiles.push({ zoom, tileX, tileY });
    }
    const keys = new Set<PlaceClusterId>();
    for (const cluster of this.serverClusters.values()) {
      if (changedTiles.some(tile => tilesOverlap(tile, cluster))) {
        keys.add(placeClusterId(this.key, cluster));
      }
    }
    return keys;
  }

  private objectIdsForClusters(
    clusterIds: Iterable<PlaceClusterId>,
  ): Set<number> {
    const changedTiles: MapTile[] = [];
    for (const id of clusterIds) {
      const [featureKey, zoom, tileX, tileY] = parsePlaceClusterId(id);
      if (featureKey === this.key) changedTiles.push({ zoom, tileX, tileY });
    }
    const objectIds = new Set<number>();
    for (const [objectId, object] of this.objectsById) {
      if (changedTiles.some(tile => pointInTile(object.lat, object.lng, tile))) {
        objectIds.add(objectId);
      }
    }
    return objectIds;
  }

  private pruneSelectionCoordinates(
    changedObjectIds?: Iterable<number>,
  ): void {
    const objectIds = changedObjectIds ?? this.selectionCoordinates.keys();
    for (const objectId of objectIds) {
      if (!this.isObjectSelected(objectId)) {
        this.selectionCoordinates.delete(objectId);
      }
    }
  }

  private pointForObject(
    objectId: number,
  ): { readonly lat: number; readonly lng: number } | undefined {
    return this.objectsById.get(objectId) ?? this.selectionCoordinates.get(objectId);
  }

  private isMapObjectSelected(object: TObject): boolean {
    return this.selection.isObjectSelected(
      this.key,
      object.id,
      object.lat,
      object.lng,
    );
  }

  private rememberSelectionCoordinates(
    objects: readonly MapSelectionObject[],
  ): void {
    for (const object of objects) {
      this.selectionCoordinates.set(object.id, {
        placeId: object.placeId,
        lat: object.lat,
        lng: object.lng,
      });
    }
  }

  private rememberVisibleSelectionCoordinates(
    objectIds: Iterable<number>,
  ): void {
    for (const objectId of objectIds) {
      if (!this.selection.hasObject(objectId)) continue;
      const object = this.objectsById.get(objectId);
      if (!object) continue;
      this.selectionCoordinates.set(objectId, {
        placeId: object.placeId,
        lat: object.lat,
        lng: object.lng,
      });
    }
  }

  private cachedDetails(object: TObject): MapObjectDetails | undefined {
    return this.cache.get<MapObjectDetails>('map-object-details', this.detailsKey(object));
  }

  private detailsKey(object: TObject): string {
    return `${this.key}:${this.objectId(object)}`;
  }

}

function tilesOverlap(left: MapTile, right: MapTile): boolean {
  return tileContains(left, right) || tileContains(right, left);
}

function tileContains(parent: MapTile, child: MapTile): boolean {
  if (parent.zoom > child.zoom) return false;
  const scale = 2 ** (child.zoom - parent.zoom);
  return Math.floor(child.tileX / scale) === parent.tileX
    && Math.floor(child.tileY / scale) === parent.tileY;
}

function pointInTile(lat: number, lng: number, tile: MapTile): boolean {
  const pointTile = latLngToTile(lat, lng, tile.zoom);
  return pointTile.tileX === tile.tileX && pointTile.tileY === tile.tileY;
}
